<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue';

const isMenuOpen = ref(false);
const hasScrolled = ref(false);

const capabilities = [
  {
    code: 'APP',
    title: '原生应用',
    description: '使用 ZPK 应用规范构建深度融入系统的 NAS 应用，获得窗口、文件和设备能力。',
    tags: ['ZPK', '系统 API', '应用商店'],
  },
  {
    code: 'WEB',
    title: 'Web App',
    description: '将成熟的 Web 服务快速带入 Zettlab 桌面，以安全沙箱运行并拥有独立应用体验。',
    tags: ['URL 接入', '安全沙箱', '零打包'],
  },
  {
    code: 'AI',
    title: '智能 Agent',
    description: '连接本地数据与设备工具，构建可理解用户意图并执行复杂任务的智能能力。',
    tags: ['Agent', 'Tools', '本地 AI'],
  },
];

const journey = [
  ['01', '了解规范', '从平台约定、权限模型和设计规范开始。'],
  ['02', '构建应用', '选择原生应用、Web App 或 Agent 接入方式。'],
  ['03', '质量验证', '通过自动化用例、兼容性与安全检查。'],
  ['04', '发布分发', '提交审核，让应用触达 Zettlab 用户。'],
];

const resources = [
  { label: '开发指南', meta: 'Guide', status: '即将开放' },
  { label: 'API 参考', meta: 'Reference', status: '即将开放' },
  { label: '设计资源', meta: 'Design Kit', status: '筹备中' },
];

function updateScrollState() {
  hasScrolled.value = window.scrollY > 16;
}

function closeMenu() {
  isMenuOpen.value = false;
}

onMounted(() => {
  updateScrollState();
  window.addEventListener('scroll', updateScrollState, { passive: true });
});

onBeforeUnmount(() => window.removeEventListener('scroll', updateScrollState));
</script>

<template>
  <div class="site-shell">
    <header class="site-header" :class="{ 'site-header--scrolled': hasScrolled }">
      <a class="brand" href="#top" aria-label="Zettlab 开放平台首页" @click="closeMenu">
        <span class="brand__mark" aria-hidden="true"><i /></span>
        <span class="brand__name">zettlab</span>
        <span class="brand__divider" />
        <span class="brand__platform">开放平台</span>
      </a>

      <button
        class="menu-button"
        type="button"
        :aria-expanded="isMenuOpen"
        aria-label="切换导航菜单"
        @click="isMenuOpen = !isMenuOpen"
      >
        <span />
        <span />
      </button>

      <nav class="site-nav" :class="{ 'site-nav--open': isMenuOpen }" aria-label="主导航">
        <a href="#capabilities" @click="closeMenu">开放能力</a>
        <a href="#workflow" @click="closeMenu">开发流程</a>
        <a href="#resources" @click="closeMenu">开发资源</a>
        <a class="nav-cta" href="#join" @click="closeMenu">加入生态</a>
      </nav>
    </header>

    <main id="top">
      <section class="hero section-frame">
        <div class="hero__glow hero__glow--one" />
        <div class="hero__glow hero__glow--two" />

        <div class="hero__content reveal">
          <div class="eyebrow"><span /> Zettlab Developer Platform</div>
          <h1>让好应用，<br /><em>自然融入</em>每一台 NAS</h1>
          <p>
            面向开发者的统一开放平台。从 Web 服务到原生应用，从系统能力到智能 Agent，
            用更短的路径构建下一代私有云体验。
          </p>
          <div class="hero__actions">
            <a class="button button--primary" href="#capabilities">
              探索开放能力
              <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M4 10h11M11 6l4 4-4 4" /></svg>
            </a>
            <a class="button button--ghost" href="#workflow">查看接入流程</a>
          </div>
          <div class="hero__note"><span /> 平台能力正在逐步开放</div>
        </div>

        <div class="hero__visual" aria-hidden="true">
          <div class="orbit orbit--outer" />
          <div class="orbit orbit--inner" />
          <div class="platform-core">
            <span class="brand__mark brand__mark--large"><i /></span>
            <strong>Zettlab OS</strong>
            <small>Open capabilities</small>
          </div>
          <div class="floating-card floating-card--app"><b>01</b><span>Native App</span></div>
          <div class="floating-card floating-card--web"><b>02</b><span>Web App</span></div>
          <div class="floating-card floating-card--agent"><b>03</b><span>AI Agent</span></div>
          <span class="orbit-dot orbit-dot--one" />
          <span class="orbit-dot orbit-dot--two" />
          <span class="orbit-dot orbit-dot--three" />
        </div>
      </section>

      <section id="capabilities" class="capabilities section-frame section-block">
        <div class="section-heading">
          <div>
            <span class="section-index">01 / CAPABILITIES</span>
            <h2>一种平台，三种构建方式</h2>
          </div>
          <p>按照产品形态选择合适的接入路径，共享一致的账号、分发与质量体系。</p>
        </div>

        <div class="capability-grid">
          <article v-for="(item, index) in capabilities" :key="item.code" class="capability-card">
            <div class="capability-card__top">
              <span class="capability-card__code">{{ item.code }}</span>
              <span class="capability-card__number">0{{ index + 1 }}</span>
            </div>
            <h3>{{ item.title }}</h3>
            <p>{{ item.description }}</p>
            <div class="tag-list"><span v-for="tag in item.tags" :key="tag">{{ tag }}</span></div>
          </article>
        </div>
      </section>

      <section id="workflow" class="workflow section-block">
        <div class="section-frame workflow__inner">
          <div class="section-heading section-heading--light">
            <div>
              <span class="section-index">02 / WORKFLOW</span>
              <h2>从想法到用户，只需四步</h2>
            </div>
            <p>开放平台将开发、测试与发布串成一条清晰路径。</p>
          </div>

          <ol class="journey-list">
            <li v-for="step in journey" :key="step[0]">
              <span class="journey-list__number">{{ step[0] }}</span>
              <div><h3>{{ step[1] }}</h3><p>{{ step[2] }}</p></div>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M14 7l5 5-5 5" /></svg>
            </li>
          </ol>
        </div>
      </section>

      <section id="resources" class="resources section-frame section-block">
        <div class="section-heading">
          <div>
            <span class="section-index">03 / RESOURCES</span>
            <h2>开发资源</h2>
          </div>
          <p>规范、接口与设计资源会随平台能力一起持续更新。</p>
        </div>

        <div class="resource-list">
          <div v-for="resource in resources" :key="resource.label" class="resource-item">
            <span class="resource-item__meta">{{ resource.meta }}</span>
            <strong>{{ resource.label }}</strong>
            <span class="resource-item__status">{{ resource.status }}</span>
          </div>
        </div>
      </section>

      <section id="join" class="join section-frame section-block">
        <div class="join__panel">
          <span class="section-index">BUILD THE NEXT</span>
          <h2>一起构建 NAS 的下一种可能</h2>
          <p>Zettlab 开放平台正在持续建设中。我们期待与开发者共同定义更自然、更智能的私有云体验。</p>
          <a class="button button--light" href="#resources">
            关注开放进度
            <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M4 10h11M11 6l4 4-4 4" /></svg>
          </a>
        </div>
      </section>
    </main>

    <footer class="site-footer section-frame">
      <div class="brand brand--footer">
        <span class="brand__mark" aria-hidden="true"><i /></span>
        <span class="brand__name">zettlab</span>
      </div>
      <p>让技术回到生活本身。</p>
      <span>© {{ new Date().getFullYear() }} Zettlab</span>
    </footer>
  </div>
</template>
